import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Add01Icon, MinusSignIcon } from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: "What are credits?",
    answer:
      "Credits are the currency you use to run AI models in your pipelines. Each model consumes a different number of credits based on its complexity and output type. For example, generating a text response costs 1-3 credits, while generating a video clip costs 15-30 credits.",
  },
  {
    question: "Can I buy more credits?",
    answer:
      "Yes! You can purchase additional credit packs at any time. Extra credits are available at $10 per 2,000 credits for Pro users and $10 per 3,000 credits for Max users. These credits never expire.",
  },
  {
    question: "What happens when I run out of credits?",
    answer:
      "Your existing workflows and saved projects remain accessible. You just won't be able to run new generations until your credits renew at the start of your next billing cycle, or until you purchase additional credits.",
  },
  {
    question: "Do unused credits roll over?",
    answer:
      "Monthly credits reset at the start of each billing cycle and don't roll over. However, any additional credits you purchase separately never expire and carry over indefinitely.",
  },
  {
    question: "Can I cancel anytime?",
    answer:
      "Absolutely. You can cancel your subscription at any time from your account settings. You'll continue to have access to your plan's features until the end of your current billing period.",
  },
  // {
  //   question: "Is there a free trial for paid plans?",
  //   answer:
  //     "Yes, both Pro and Max plans come with a 14-day free trial. No credit card required to start. You'll get the full credit allocation during your trial period.",
  // },
  {
    question: "Can I switch plans?",
    answer:
      "You can upgrade or downgrade at any time. When upgrading, the price difference is prorated for the remainder of your billing cycle. When downgrading, the change takes effect at the start of your next billing cycle.",
  },
];

function FAQAccordionItem({
  item,
  isOpen,
  onToggle,
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-border border-b last:border-b-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between py-5 text-left"
      >
        <span
          className={cn(
            "text-sm font-medium transition-colors sm:text-base",
            isOpen ? "text-foreground" : "text-foreground/80",
          )}
        >
          {item.question}
        </span>
        <HugeiconsIcon
          icon={isOpen ? MinusSignIcon : Add01Icon}
          className="text-muted-foreground ml-4 size-4 shrink-0"
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="text-muted-foreground pb-5 text-sm leading-relaxed">
              {item.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="border-border border-t py-20 sm:py-28">
      <div className="mx-auto max-w-2xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mb-10 text-center"
        >
          <p className="text-brand mb-4 text-sm font-semibold tracking-wide uppercase">
            FAQ
          </p>
          <h2 className="font-display text-foreground text-3xl font-normal tracking-tight sm:text-4xl">
            Frequently asked questions
          </h2>
          <p className="text-muted-foreground mt-3 text-base">
            Everything you need to know about our pricing.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="border-border border-t"
        >
          {faqItems.map((item, i) => (
            <FAQAccordionItem
              key={i}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
